import type { StateStorage } from 'zustand/middleware';

interface Envelope {
  storedAt: number;
  payload: string;
}

/**
 * sessionStorage wrapper that also expires entries after `ttlMs`.
 * sessionStorage alone clears on browser/tab close; this adds a TTL
 * so long-lived tabs don't keep stale checkout state around.
 */
export function createExpiringSessionStorage(ttlMs: number): StateStorage {
  return {
    getItem: (name) => {
      if (typeof window === 'undefined') return null;
      const raw = window.sessionStorage.getItem(name);
      if (!raw) return null;
      try {
        const env = JSON.parse(raw) as Envelope;
        if (!env || typeof env.storedAt !== 'number' || typeof env.payload !== 'string') {
          window.sessionStorage.removeItem(name);
          return null;
        }
        if (Date.now() - env.storedAt > ttlMs) {
          window.sessionStorage.removeItem(name);
          return null;
        }
        return env.payload;
      } catch {
        window.sessionStorage.removeItem(name);
        return null;
      }
    },
    setItem: (name, value) => {
      if (typeof window === 'undefined') return;
      const env: Envelope = { storedAt: Date.now(), payload: value };
      window.sessionStorage.setItem(name, JSON.stringify(env));
    },
    removeItem: (name) => {
      if (typeof window === 'undefined') return;
      window.sessionStorage.removeItem(name);
    },
  };
}

export const ONE_DAY_MS = 24 * 60 * 60 * 1000;
